import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "../../../i18n/LanguageContext";
import "../css/Common.css";
import "./css/Dashboard.css";
import "./css/DashboardSimple.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faThumbtack,
  faCheckCircle,
  faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import UserSettingsAPI from "../services/UserSettingsAPI";
import { useUser } from "../context/UserContext";
import { useAccountSetup } from "../context/AccountSetupContext";
import { useRoleCheck } from "../../../hooks/useRoleCheck";
import SafetyScoreCard from "./components/SafetyScoreCard";
import HelpOptionsFooter from "../common/HelpOptionsFooter";
import TrainingProgress from "./components/TrainingProgress";
import TextSimulations from "./components/TextSimulations";
import EmailSimulations from "./components/EmailSimulations";
import RecentActivities from "./components/RecentActivities";
import Recommendations from "./components/Recommendations";
import TodaysFocusCard from "./components/TodaysFocusCard";
import ShieldScoreWidget from "./components/ShieldScoreWidget";
import AccountSetupJourney from "./AccountSetupJourney";
import TrialReminderModal from "./components/TrialReminderModal";
import {
  shouldShowTrialReminder,
  markTrialReminderShown,
} from "../../../utils/trialReminderUtils";
import SubscriptionsAPI from "../services/SubscriptionsAPI";

function Dashboard({ user, setHeroContent, suppressBanner = false }) {
  const {
    selectedUser,
    loggedInUser,
    isReadOnlyView: isReadOnlyViewCtx,
    exitReadOnlyView,
  } = useUser();
  // Prefer context value (authoritative); accept prop as a fallback for test/override
  const isReadOnlyView = isReadOnlyViewCtx || false;
  const {
    isAccountOwner,
    isLoading: setupLoading,
    setupJourneyOnly,
    getProgressPercentage,
  } = useAccountSetup();
  const { isPlatformAdmin } = useRoleCheck(loggedInUser);
  const { t } = useTranslation();
  const [userName, setUserName] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [showTrialReminder, setShowTrialReminder] = useState(false);
  const [trialReminderData, setTrialReminderData] = useState(null);

  // ─── Setup Journey visibility (icon-controlled above threshold) ───────────
  const [journeyVisible, setJourneyVisible] = useState(false);
  const progressPercentage =
    isAccountOwner && !setupLoading ? getProgressPercentage() : 0;
  const journeyThresholdMet = progressPercentage > 90;

  // ─── Pin-as-default state ──────────────────────────────────────────────────
  const [isDefaultPage, setIsDefaultPage] = useState(
    () => (localStorage.getItem("landingPage") || "Dashboard") === "Dashboard",
  );
  const [pinConfirmed, setPinConfirmed] = useState(false);

  const handlePinAsDefault = () => {
    localStorage.setItem("landingPage", "Dashboard");
    UserSettingsAPI.updateSettings({
      defaultLandingPage: "Dashboard",
    }).catch(() => {});
    setIsDefaultPage(true);
    setPinConfirmed(true);
    setTimeout(() => setPinConfirmed(false), 2500);
    window.dispatchEvent(new Event("landingPageChanged"));
  };

  // Re-sync when another page changes the default
  useEffect(() => {
    const sync = () =>
      setIsDefaultPage(
        (localStorage.getItem("landingPage") || "Dashboard") === "Dashboard",
      );
    window.addEventListener("landingPageChanged", sync);
    return () => window.removeEventListener("landingPageChanged", sync);
  }, []);

  useEffect(() => {
    // Determine which user data to use
    let targetUser = null;

    if (user) {
      targetUser = user;
    } else if (isReadOnlyView && selectedUser) {
      // Read-only view: show the selected member's info
      targetUser = selectedUser;
    } else if (loggedInUser) {
      // Normal mode: always use the logged-in user
      targetUser = loggedInUser;
    } else {
      // Fallback to localStorage
      const firstName = localStorage.getItem("firstName");
      const lastName = localStorage.getItem("lastName");
      const storedUserName = localStorage.getItem("userName");

      if (firstName || lastName || storedUserName) {
        targetUser = {
          firstName: firstName || "",
          lastName: lastName || "",
          userName: storedUserName || "",
        };
      }
    }

    setCurrentUser(targetUser);

    // Set display name (first name only for cleaner greeting)
    if (targetUser) {
      if (targetUser.firstName) {
        setUserName(targetUser.firstName);
      } else if (targetUser.userName) {
        setUserName(targetUser.userName);
      } else {
        setUserName("User");
      }
    } else {
      setUserName("Guest");
    }
  }, [user, selectedUser, loggedInUser, isReadOnlyView]);

  // Check for trial reminder on dashboard load
  useEffect(() => {
    const checkTrialReminder = async () => {
      try {
        const response = await SubscriptionsAPI.getCurrentSubscription();
        if (response && response.data) {
          const reminderData = shouldShowTrialReminder(response.data);
          if (reminderData) {
            setTrialReminderData(reminderData);
            setShowTrialReminder(true);
          }
        }
      } catch (error) {
        console.error("Error checking trial reminder:", error);
      }
    };

    checkTrialReminder();
  }, []);

  const handleCloseTrialReminder = () => {
    setShowTrialReminder(false);
    markTrialReminderShown();
  };

  // Read-only banner component for when account owner views member's dashboard
  const ReadOnlyBanner = () => {
    if (!isReadOnlyView || suppressBanner) return null;
    return (
      <div
        className="common-message info"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          marginBottom: 16,
          borderLeft: "4px solid var(--info-color, #17a2b8)",
          backgroundColor: "var(--info-bg, rgba(23, 162, 184, 0.1))",
          borderRadius: 6,
        }}
      >
        <span>
          <strong>Read-Only View</strong> — Viewing{" "}
          {selectedUser
            ? `${selectedUser.firstName} ${selectedUser.lastName}'s`
            : "member's"}{" "}
          dashboard. You cannot make changes in this mode.
        </span>
        <button
          className="common-button small secondary"
          onClick={() => {
            if (setHeroContent) setHeroContent("Member User Info");
            exitReadOnlyView();
          }}
          style={{ marginLeft: 16, whiteSpace: "nowrap" }}
        >
          Return to MyProfile
        </button>
      </div>
    );
  };

  return (
    <div className="simple-dashboard-container">
      <ReadOnlyBanner />

      <div className="simple-header">
        <div className="simple-header-content">
          <div className="simple-header-main">
            <h1>
              {t("dashboard.welcome")}, {userName}
            </h1>
            <p className="subtitle">{t("dashboard.subtitle")}</p>
          </div>
          <div className="simple-header-actions">
            {/* TODO: Re-enable Report a Scam button once reporting flow is ready
            <button
              className="simple-report-button"
              onClick={() => setHeroContent && setHeroContent("Shield Reports")}
            >
              📊 Report a Scam
            </button>
            */}
            {isAccountOwner &&
              !isReadOnlyView &&
              !setupLoading &&
              journeyThresholdMet && (
                <button
                  className={`setup-journey-toggle-btn${journeyVisible ? " active" : ""}`}
                  onClick={() => setJourneyVisible((v) => !v)}
                  title={
                    journeyVisible
                      ? t("dashboard.setupProgress.hide")
                      : t("dashboard.setupProgress.show")
                  }
                  aria-label={
                    journeyVisible
                      ? t("dashboard.setupProgress.hideAriaLabel")
                      : t("dashboard.setupProgress.showAriaLabel")
                  }
                >
                  <FontAwesomeIcon icon={faClipboardList} />
                  <span className="setup-journey-toggle-label">
                    {t("dashboard.setupProgress.label")}
                  </span>
                </button>
              )}
          </div>
        </div>
      </div>

      {/* Account Setup Journey - inline when below threshold, icon-controlled when above */}
      {isAccountOwner &&
        !setupLoading &&
        (!journeyThresholdMet || journeyVisible) && (
          <div className="simple-journey-wrapper">
            <AccountSetupJourney setHeroContent={setHeroContent} />
          </div>
        )}

      {/* Today's Focus — single most important action, always visible */}
      {!setupJourneyOnly && !isReadOnlyView && (
        <TodaysFocusCard setHeroContent={setHeroContent} />
      )}

      {/* Dashboard panels - hidden when setupJourneyOnly is true (progress <= 90%) */}
      {!setupJourneyOnly && (
        <>
          <div className="simple-main-grid">
            <SafetyScoreCard setHeroContent={setHeroContent} />
            <TrainingProgress
              setHeroContent={setHeroContent}
              completed={7}
              total={12}
              categories={{
                emails: { completed: 3, total: 4 },
                sms: { completed: 2, total: 3 },
                qr: { completed: 1, total: 2 },
                voice: { completed: 1, total: 2 },
                socialMedia: { completed: 0, total: 1 },
              }}
            />
            <TextSimulations />
            <EmailSimulations />
            {isPlatformAdmin() && (
              <ShieldScoreWidget setHeroContent={setHeroContent} />
            )}
          </div>

          {/* Commenting these two cards out for now, until we develop content for them.
          <div className="simple-main-grid">
            <RecentActivities />
            <Recommendations />
          </div> 
          */}
        </>
      )}

      <HelpOptionsFooter
        className="simple-footer"
        onContactSupport={() =>
          setHeroContent && setHeroContent("ContactSupport")
        }
      />

      {showTrialReminder && trialReminderData && (
        <TrialReminderModal
          trialEndDate={trialReminderData.trialEndDate}
          daysRemaining={trialReminderData.daysRemaining}
          onClose={handleCloseTrialReminder}
          onViewSubscriptions={() => setHeroContent("Account & Billing")}
          onViewPayments={() => setHeroContent("Payments:Methods")}
          isAccountOwner={isAccountOwner}
          onAlertAccountOwner={() => {
            // TODO: Implement alert account owner functionality
            console.log("Alert account owner clicked");
          }}
        />
      )}
    </div>
  );
}

export default Dashboard;
